import React from 'react';
import { Link } from 'react-router-dom';
import { Phone, Mail, MapPin, Clock, Instagram } from 'lucide-react';
import { trackNavigation, trackFeatureInteraction, trackExternalLink } from '../../utils/analytics';

const Footer: React.FC = () => {
  return (
    <footer className="bg-secondary-900 text-white">
      <div className="container mx-auto px-4 py-12">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          <div>
            <h3 className="text-lg font-heading font-bold mb-4 text-white">Seidels Immobilien</h3>
            <p className="text-secondary-200 mb-4">
              Ihr zuverlässiger Partner für Immobilien in Berlin und Brandenburg.
            </p>
            <div className="flex items-center space-x-2 mb-4">
              <img 
                src="/logo192.png" 
                alt="Seidels Immobilien Berlin Brandenburg - Immobilien verkaufen Kapitalanlage Makler" 
                className="w-8 h-8 object-contain"
              />
              <span className="text-primary-300 font-semibold">Oscar Seidel</span>
            </div>
            <div className="flex items-center space-x-2">
              <a
                href="https://www.instagram.com/immo.seidel/"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center justify-center w-10 h-10 bg-primary-600 hover:bg-primary-700 rounded-full transition-colors"
                aria-label="Instagram - immo.seidel"
                onClick={() => trackExternalLink('https://www.instagram.com/immo.seidel/', 'Instagram', 'Footer')}
              >
                <Instagram size={20} className="text-white" />
              </a>
              <span className="text-secondary-200 text-sm">Folgen Sie uns auf Instagram</span>
            </div>
          </div>

          <div>
            <h4 className="text-lg font-semibold mb-4 text-white">Kontakt</h4>
            <div className="space-y-3">
              <a
                href="tel:017632135694"
                className="flex items-center space-x-2 text-secondary-200 hover:text-white transition-colors"
                onClick={() => {
                  trackFeatureInteraction('Contact', 'Phone Call', { location: 'Footer', phoneNumber: '0176 321 35 694' });
                  try {
                    const c = localStorage.getItem('cookieConsent');
                    const consent = c ? JSON.parse(c) : null;
                    if (consent && consent.marketing && (window as any).fbq) {
                      (window as any).fbq('track', 'Contact');
                    }
                  } catch {}
                }}
              >
                <Phone size={16} />
                <span>0176 321 35 694</span>
              </a>
              <a
                href="mailto:oscar@seidelsimmobilien.de"
                className="flex items-center space-x-2 text-secondary-200 hover:text-white transition-colors"
                onClick={() => {
                  trackFeatureInteraction('Contact', 'Email', { location: 'Footer', email: 'oscar@seidelsimmobilien.de' });
                  try {
                    const c = localStorage.getItem('cookieConsent');
                    const consent = c ? JSON.parse(c) : null;
                    if (consent && consent.marketing && (window as any).fbq) {
                      (window as any).fbq('track', 'Contact');
                    }
                  } catch {}
                }}
              >
                <Mail size={16} />
                <span>oscar@seidelsimmobilien.de</span>
              </a>
              <div className="flex items-start space-x-2 text-secondary-200">
                <MapPin size={16} className="mt-1" />
                <span>Freienwalder Chaussee 26<br />16356 Werneuchen</span>
              </div>
            </div>
          </div>

          <div>
            <h4 className="text-lg font-semibold mb-4 text-white">Öffnungszeiten</h4>
            <div className="space-y-2 text-secondary-200">
              <div className="flex items-center space-x-2">
                <Clock size={16} />
                <span>Mo - Fr: 09:00 - 18:00</span>
              </div>
              <div className="flex items-center space-x-2">
                <Clock size={16} />
                <span>Sa: 09:00 - 13:00</span>
              </div>
              <div className="flex items-center space-x-2">
                <Clock size={16} />
                <span>So: Geschlossen</span>
              </div>
              <p className="text-sm mt-2">
                Termine auch abends nach Vereinbarung möglich
              </p>
            </div>
          </div>

          <div>
            <h4 className="text-lg font-semibold mb-4 text-white">Navigation</h4>
            <nav className="space-y-2">
              <Link
                to="/"
                className="block text-secondary-200 hover:text-white transition-colors"
                onClick={() => trackNavigation('Startseite', '/', 'Footer')}
              >
                Startseite
              </Link>
              <Link
                to="/kapitalanlage"
                className="block text-secondary-200 hover:text-white transition-colors"
                onClick={() => trackNavigation('Kapitalanlage', '/kapitalanlage', 'Footer')}
              >
                Kapitalanlage
              </Link>
              <Link
                to="/uber-uns"
                className="block text-secondary-200 hover:text-white transition-colors"
                onClick={() => trackNavigation('Über uns', '/uber-uns', 'Footer')}
              >
                Über uns
              </Link>
              <Link
                to="/einzugsgebiete"
                className="block text-secondary-200 hover:text-white transition-colors"
                onClick={() => trackNavigation('Einzugsgebiete', '/einzugsgebiete', 'Footer')}
              >
                Einzugsgebiete
              </Link>
              <Link
                to="/faq"
                className="block text-secondary-200 hover:text-white transition-colors"
                onClick={() => trackNavigation('FAQ', '/faq', 'Footer')}
              >
                Häufige Fragen
              </Link>
              <Link
                to="/blog"
                className="block text-secondary-200 hover:text-white transition-colors"
                onClick={() => trackNavigation('Blog', '/blog', 'Footer')}
              >
                Blog
              </Link>
              <Link
                to="/kontakt"
                className="block text-secondary-200 hover:text-white transition-colors"
                onClick={() => {
                  trackNavigation('Kontakt', '/kontakt', 'Footer');
                  try {
                    const c = localStorage.getItem('cookieConsent');
                    const consent = c ? JSON.parse(c) : null;
                    if (consent && consent.marketing && (window as any).fbq) {
                      (window as any).fbq('track', 'Contact');
                    }
                  } catch {}
                }}
              >
                Kontakt
              </Link>
              <Link
                to="/checkliste"
                className="block text-secondary-200 hover:text-white transition-colors"
                onClick={() => trackNavigation('Checkliste', '/checkliste', 'Footer')}
              >
                Gratis Checkliste
              </Link>
              <Link
                to="/newsletter"
                className="block text-secondary-200 hover:text-white transition-colors"
                onClick={() => trackNavigation('Newsletter', '/newsletter', 'Footer')}
              >
                Newsletter
              </Link>
              <Link
                to="/impressum"
                className="block text-secondary-200 hover:text-white transition-colors"
                onClick={() => trackNavigation('Impressum', '/impressum', 'Footer')}
              >
                Impressum
              </Link>
              <Link
                to="/datenschutz"
                className="block text-secondary-200 hover:text-white transition-colors"
                onClick={() => trackNavigation('Datenschutz', '/datenschutz', 'Footer')}
              >
                Datenschutz
              </Link>
            </nav>
          </div>
        </div>

        <div className="border-t border-secondary-700 mt-8 pt-8 text-center text-secondary-400">
          <p>&copy; 2025 Seidels Immobilien. Alle Rechte vorbehalten.</p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
